import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Typography,
    makeStyles,
} from '@material-ui/core';
import React, { useState } from 'react';
import { CustomTextField } from '../../Textfield';
import { OTHER_REASON_MIN_LENGTH, REVEAL_REASON_CODES } from './useKycReveal';

const useStyles = makeStyles(() => ({
    titleText: { fontSize: 18, fontWeight: 700 },
    subtitleText: { fontSize: 13, color: '#6B7280', marginTop: 4 },
    field: { width: '100%', marginTop: 16 },
    notice: {
        fontSize: 12,
        color: '#B45309',
        marginTop: 16,
        lineHeight: 1.5,
    },
}));

export interface KycRevealReasonModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (reasonCode: string, reasonText?: string) => void;
    /** e.g. "Aadhaar image" — names what is about to be shown. */
    subject: string;
    loading?: boolean;
}

const KycRevealReasonModal: React.FC<KycRevealReasonModalProps> = ({
    open,
    onClose,
    onConfirm,
    subject,
    loading,
}) => {
    const classes = useStyles();
    const [reasonCode, setReasonCode] = useState('');
    const [reasonText, setReasonText] = useState('');

    const needsText = reasonCode === 'OTHER';
    const textTooShort =
        needsText && reasonText.trim().length < OTHER_REASON_MIN_LENGTH;
    const canSubmit = !!reasonCode && !textTooShort && !loading;

    const handleClose = () => {
        setReasonCode('');
        setReasonText('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography>
                <Typography className={classes.titleText}>
                    Reveal {subject}
                </Typography>
                <Typography className={classes.subtitleText}>
                    A reason is required and is recorded against your name.
                </Typography>
            </DialogTitle>

            <DialogContent>
                <CustomTextField
                    select
                    label="Reason"
                    value={reasonCode}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setReasonCode(e.target.value)
                    }
                    variant="outlined"
                    className={classes.field}
                >
                    {REVEAL_REASON_CODES.map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                            {r.label}
                        </MenuItem>
                    ))}
                </CustomTextField>

                {needsText && (
                    <CustomTextField
                        multiline
                        rows={3}
                        label={`Describe the reason (min ${OTHER_REASON_MIN_LENGTH} characters)`}
                        value={reasonText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setReasonText(e.target.value)
                        }
                        variant="outlined"
                        className={classes.field}
                        error={!!reasonText && textTooShort}
                        helperText={
                            reasonText && textTooShort
                                ? `${OTHER_REASON_MIN_LENGTH - reasonText.trim().length} more characters needed`
                                : ' '
                        }
                    />
                )}

                <Typography className={classes.notice}>
                    This reveal is logged with your user id, the document and
                    the time. The value stays visible for 2 minutes and is never
                    included in any export.
                </Typography>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={!canSubmit}
                    onClick={() => {
                        onConfirm(
                            reasonCode,
                            needsText ? reasonText.trim() : undefined,
                        );
                        setReasonCode('');
                        setReasonText('');
                    }}
                >
                    Reveal
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default KycRevealReasonModal;
